import { list } from '@vercel/blob';

export interface BlobItem {
  url: string;
  pathname: string;
  uploadedAt?: string;
}

interface BlobResponse {
  blobs: BlobItem[];
  hasMore: boolean;
}

// 通用请求函数
export const makeBlobRequest = async (queryParams: Record<string, string>): Promise<BlobResponse> => {
  const token = process.env.REACT_APP_BLOB_READ_WRITE_TOKEN;
  
  if (!token) {
    throw new Error('Blob token not found');
  }

  const params = new URLSearchParams(queryParams).toString();
  const response = await fetch(`/blob-api?${params}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Accept': 'application/json'
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
};

// 前台轮播图获取方法
export const getHomeBanners = async () => {
  try {
    const token = process.env.REACT_APP_BLOB_READ_WRITE_TOKEN;
    
    console.log('BlobService - Environment check:', {
      token: token ? '存在' : '不存在',
      tokenLength: token?.length || 0,
      environment: process.env.NODE_ENV
    });

    if (!token) {
      throw new Error('Blob token not found in environment variables');
    }

    // 构建完整的查询 URL
    const queryParams = new URLSearchParams({
      limit: '100', // 增加限制，确保能获取所有图片
      prefix: 'banners/'
    }).toString();

    const response = await fetch(`/blob-api?${queryParams}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json'
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: BlobResponse = await response.json();
    console.log('BlobService - Raw response:', data);

    // 按文件夹组织轮播图数据
    // 新的文件结构: banners/{banner-id}/web.jpg 和 banners/{banner-id}/mobile.jpg
    const bannerFolders = new Map<string, { web?: BlobItem; mobile?: BlobItem; name: string; order?: number }>();
    
    // 过滤出目录和图片
    data.blobs.forEach(blob => {
      // 跳过根目录
      if (blob.pathname === 'banners/') return;
      
      const parts = blob.pathname.split('/');
      
      // 处理二级目录结构，如：banners/banner-1/
      if (parts.length >= 2) {
        const folderName = parts[1]; // banner-1
        
        // 是目录还是文件
        if (parts.length === 2 || blob.pathname.endsWith('/')) {
          // 是目录，如 banners/banner-1/
          // 提取folder名称中的排序信息（如果存在）
          let name = folderName;
          let order = 1;
          
          // 尝试从文件夹名称中提取顺序，格式如 banner-1_order5
          const orderMatch = folderName.match(/_order(\d+)$/);
          if (orderMatch) {
            order = parseInt(orderMatch[1], 10);
            name = folderName.replace(/_order\d+$/, '');
          }
          
          if (!bannerFolders.has(folderName)) {
            bannerFolders.set(folderName, { name, order, web: undefined, mobile: undefined });
          }
        } else if (parts.length === 3) {
          // 是文件，如 banners/banner-1/web.jpg
          const folderName = parts[1]; // banner-1
          const fileName = parts[2]; // web.jpg 或 mobile.jpg
          
          if (!bannerFolders.has(folderName)) {
            // 从文件夹名提取名称和顺序信息
            let name = folderName;
            let order = 1;
            
            const orderMatch = folderName.match(/_order(\d+)$/);
            if (orderMatch) {
              order = parseInt(orderMatch[1], 10);
              name = folderName.replace(/_order\d+$/, '');
            }
            
            bannerFolders.set(folderName, { name, order, web: undefined, mobile: undefined });
          }
          
          // 根据文件名判断是web还是mobile版本
          if (fileName.includes('web') || fileName === 'web.jpg' || fileName === 'web.png') {
            bannerFolders.get(folderName)!.web = blob;
          } else if (fileName.includes('mobile') || fileName === 'mobile.jpg' || fileName === 'mobile.png') {
            bannerFolders.get(folderName)!.mobile = blob;
          } else {
            // 如果没有明确的标识，将第一个图片作为web版本
            if (!bannerFolders.get(folderName)!.web) {
              bannerFolders.get(folderName)!.web = blob;
            }
          }
        }
      }
    });
    
    console.log('BlobService - Banner folders:', Array.from(bannerFolders.entries()));
    
    // 转换为前台需要的格式
    const banners = Array.from(bannerFolders.values())
      .filter(folder => folder.web) // 至少有web版本
      .sort((a, b) => (a.order || 1) - (b.order || 1)) // 按顺序排序
      .map(folder => ({
        image: folder.web!.url, // Web端图片URL
        mobileBanner: folder.mobile?.url || '', // 移动端图片URL，如果没有则为空字符串
        title: folder.name,
        folderId: folder.web!.pathname.split('/')[1], // 文件夹ID用于管理
      }));
    
    console.log('BlobService - Processed banners:', banners);
    return banners;
  } catch (error) {
    console.error('BlobService - Error getting home banners:', error);
    return [];
  }
};

// 管理后台轮播图获取方法
export const getBannerImages = async () => {
  console.log('getBannerImages called');
  
  try {
    const token = process.env.REACT_APP_BLOB_READ_WRITE_TOKEN;
    
    console.log('BlobService - Environment check:', {
      token: token ? '存在' : '不存在',
      tokenLength: token?.length || 0
    });

    if (!token) {
      throw new Error('Blob token not found in environment variables');
    }

    // 构建完整的查询 URL
    const queryParams = new URLSearchParams({
      limit: '100', // 增加限制，确保能获取所有图片
      prefix: 'banners/'
    }).toString();

    const response = await fetch(`/blob-api?${queryParams}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json'
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: BlobResponse = await response.json();
    console.log('BlobService - API Response:', data);

    // 按文件夹组织轮播图数据
    // 新的文件结构: banners/{banner-id}/web.jpg 和 banners/{banner-id}/mobile.jpg
    const bannerFolders = new Map<string, { web?: BlobItem; mobile?: BlobItem; name: string; order?: number }>();
    
    // 过滤出目录和图片
    data.blobs.forEach(blob => {
      // 跳过根目录
      if (blob.pathname === 'banners/') return;
      
      const parts = blob.pathname.split('/');
      
      // 处理二级目录结构，如：banners/banner-1/
      if (parts.length >= 2) {
        const folderName = parts[1]; // banner-1
        
        // 是目录还是文件
        if (parts.length === 2 || blob.pathname.endsWith('/')) {
          // 是目录，如 banners/banner-1/
          // 提取folder名称中的排序信息（如果存在）
          let name = folderName;
          let order = 1;
          
          // 尝试从文件夹名称中提取顺序，格式如 banner-1_order5
          const orderMatch = folderName.match(/_order(\d+)$/);
          if (orderMatch) {
            order = parseInt(orderMatch[1], 10);
            name = folderName.replace(/_order\d+$/, '');
          }
          
          if (!bannerFolders.has(folderName)) {
            bannerFolders.set(folderName, { name, order, web: undefined, mobile: undefined });
          }
        } else if (parts.length === 3) {
          // 是文件，如 banners/banner-1/web.jpg
          const folderName = parts[1]; // banner-1
          const fileName = parts[2]; // web.jpg 或 mobile.jpg
          
          if (!bannerFolders.has(folderName)) {
            // 从文件夹名提取名称和顺序信息
            let name = folderName;
            let order = 1;
            
            const orderMatch = folderName.match(/_order(\d+)$/);
            if (orderMatch) {
              order = parseInt(orderMatch[1], 10);
              name = folderName.replace(/_order\d+$/, '');
            }
            
            bannerFolders.set(folderName, { name, order, web: undefined, mobile: undefined });
          }
          
          // 根据文件名判断是web还是mobile版本
          if (fileName.includes('web') || fileName === 'web.jpg' || fileName === 'web.png') {
            bannerFolders.get(folderName)!.web = blob;
          } else if (fileName.includes('mobile') || fileName === 'mobile.jpg' || fileName === 'mobile.png') {
            bannerFolders.get(folderName)!.mobile = blob;
          } else {
            // 如果没有明确的标识，将第一个图片作为web版本
            if (!bannerFolders.get(folderName)!.web) {
              bannerFolders.get(folderName)!.web = blob;
            }
          }
        }
      }
    });
    
    console.log('BlobService - Banner folders:', Array.from(bannerFolders.entries()));
    
    // 转换为管理后台需要的格式
    const carousels = Array.from(bannerFolders.values())
      .filter(folder => folder.web) // 至少有web版本
      .sort((a, b) => (a.order || 1) - (b.order || 1)) // 按顺序排序
      .map((folder, index) => ({
        id: folder.web!.pathname.split('/')[1], // 文件夹名作为ID
        title: folder.name,
        imageUrl: folder.web!.url,
        mobileBannerUrl: folder.mobile?.url || '',
        linkUrl: '/products/new', // 将link改为linkUrl以匹配CarouselItem接口
        order: folder.order || index + 1,
        status: 'active' as const, // 使用类型断言确保是'active'|'inactive'类型
        createdAt: folder.web!.uploadedAt,
        updatedAt: folder.web!.uploadedAt,
      }));
    
    console.log('BlobService - Processed Carousels:', carousels);
    return carousels;
  } catch (error: any) {
    console.error('BlobService - Error occurred:', error);
    throw error;
  }
};

// 上传轮播图函数
export const uploadBannerImage = async (file: File, options: { title: string; isWeb: boolean; folderId?: string; order?: number }): Promise<BlobItem> => {
  try {
    const token = process.env.REACT_APP_BLOB_READ_WRITE_TOKEN;
    const { title, isWeb, folderId, order } = options;
    
    console.log('BlobService - 开始上传轮播图:', title, isWeb ? 'web版本' : 'mobile版本');
    
    if (!token) {
      console.error('BlobService - 没有找到Blob令牌');
      throw new Error('Blob token not found in environment variables');
    }
    
    // 创建或使用现有的文件夹ID
    const folderID = folderId || `${title.replace(/\s+/g, '-')}_order${order || 1}_${Date.now()}`;
    
    // 文件名根据类型确定
    const fileExtension = file.name.split('.').pop() || 'jpg';
    const fileName = isWeb ? `web.${fileExtension}` : `mobile.${fileExtension}`;
    
    // 构建文件路径: banners/{folder-id}/{web|mobile}.jpg
    const filePath = `banners/${folderID}/${fileName}`;
    
    console.log('BlobService - 上传路径:', filePath);
    console.log('BlobService - 文件大小:', file.size, '文件类型:', file.type);
    
    // 发送请求 - 使用PUT方法而不是POST
    console.log('BlobService - 开始发送PUT请求');
    const response = await fetch(`/blob-api/${filePath}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        // 不要添加Content-Type，让浏览器自动设置为文件的MIME类型
      },
      body: file
    });
    
    console.log('BlobService - PUT请求已发送，状态码:', response.status);
    
    if (!response.ok) {
      console.error('上传失败状态码:', response.status);
      const errorText = await response.text();
      console.error('上传失败响应:', errorText);
      throw new Error(`上传失败: ${response.statusText}. 状态码: ${response.status}. 详情: ${errorText}`);
    }
    
    const data = await response.json();
    console.log('上传成功响应:', data);
    
    // 返回与BlobItem接口一致的数据
    return {
      url: data.url,
      pathname: filePath,
      uploadedAt: new Date().toISOString()
    };
  } catch (error) {
    console.error('上传轮播图失败:', error);
    if (error instanceof Error) {
      console.error('错误消息:', error.message);
      console.error('错误栈:', error.stack);
    }
    throw error;
  }
};

/**
 * 删除轮播图文件夹及其内容
 * @param folderId 文件夹ID
 * @returns 是否删除成功
 */
export const deleteBannerFolder = async (folderId: string): Promise<boolean> => {
  try {
    console.log('开始删除轮播图文件夹:', folderId);
    
    // 检查token是否存在
    const token = process.env.REACT_APP_BLOB_READ_WRITE_TOKEN;
    if (!token) {
      console.error('BlobService - Blob token not found in environment variables');
      return false;
    }
    
    if (!folderId) {
      console.error('Banner folder ID is required');
      return false;
    }
    
    // 构建完整的文件夹路径
    const folderPath = `banners/${folderId}/`;
    
    // 获取该文件夹下的所有文件
    console.log('获取文件夹内的所有文件:', folderPath);
    const response = await fetch(`/blob-api?prefix=${folderPath}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      console.error(`获取文件列表失败: ${response.status} ${response.statusText}`);
      return false;
    }
    
    const data = await response.json();
    const blobs: BlobItem[] = data.blobs || [];
    
    console.log(`找到 ${blobs.length} 个文件需要删除:`, blobs.map(b => b.pathname));
    
    if (blobs.length === 0) {
      console.warn('文件夹为空或不存在:', folderPath);
      return true; // 如果文件夹为空或不存在，视为删除成功
    }
    
    // 构建要删除的URL数组
    const urlsToDelete = blobs.map(blob => blob.url);
    
    console.log('准备删除以下URL:', urlsToDelete);
    
    // 发送删除请求
    const deleteResponse = await fetch('/blob-api/delete', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ urls: urlsToDelete }),
    });
    
    if (!deleteResponse.ok) {
      console.error(`删除请求失败: ${deleteResponse.status} ${deleteResponse.statusText}`);
      const errorText = await deleteResponse.text();
      console.error('错误详情:', errorText);
      return false;
    }
    
    const deleteResult = await deleteResponse.json();
    console.log('删除结果:', deleteResult);
    
    // 验证删除是否成功
    const verifyResponse = await fetch(`/blob-api?prefix=${folderPath}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!verifyResponse.ok) {
      console.error(`验证删除失败: ${verifyResponse.status} ${verifyResponse.statusText}`);
      return true; // 假设删除成功，因为删除请求成功了
    }
    
    const verifyData = await verifyResponse.json();
    const remainingBlobs: BlobItem[] = verifyData.blobs || [];
    
    if (remainingBlobs.length === 0) {
      console.log('验证成功：所有文件已删除');
      return true;
    } else {
      console.warn('验证失败：部分文件可能未删除', remainingBlobs);
      return false;
    }
    
  } catch (error) {
    console.error('删除轮播图文件夹时出错:', error);
    return false;
  }
};

/**
 * 删除横幅图片
 * @param pathname 图片路径或文件名
 * @returns 是否删除成功
 */
export const deleteBannerImage = async (pathname: string): Promise<boolean> => {
  try {
    console.log('开始删除轮播图:', pathname);
    
    // 检查token是否存在
    const token = process.env.REACT_APP_BLOB_READ_WRITE_TOKEN;
    if (!token) {
      console.error('BlobService - Blob token not found in environment variables');
      return false;
    }
    
    if (!pathname) {
      console.error('Banner pathname is required');
      return false;
    }
    
    // 获取所有 banners/ 目录下的文件
    console.log('获取轮播图目录下的所有文件');
    const response = await fetch(`/blob-api?prefix=banners/`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      console.error(`获取图片列表失败: ${response.status} ${response.statusText}`);
      return false;
    }
    
    const data = await response.json();
    const blobs: BlobItem[] = data.blobs || [];
    
    console.log(`找到 ${blobs.length} 个轮播图文件`);
    
    // 寻找匹配的文件
    // 1. 尝试直接匹配路径
    let targetBlobs = blobs.filter(blob => blob.pathname === pathname);
    
    // 2. 如果没找到，可能只提供了文件名，尝试匹配文件名部分
    if (targetBlobs.length === 0) {
      const filename = pathname.split('/').pop() || '';
      console.log('尝试通过文件名匹配:', filename);
      targetBlobs = blobs.filter(blob => blob.pathname.endsWith(filename));
    }
    
    // 3. 如果还是没找到，尝试模糊匹配
    if (targetBlobs.length === 0) {
      console.log('尝试模糊匹配:', pathname);
      targetBlobs = blobs.filter(blob => blob.pathname.includes(pathname) || pathname.includes(blob.pathname.split('/').pop() || ''));
    }
    
    if (targetBlobs.length === 0) {
      console.error('未找到匹配的轮播图文件:', pathname);
      return false;
    }
    
    console.log(`找到 ${targetBlobs.length} 个匹配的文件:`, targetBlobs.map(b => b.pathname));
    
    // 构建要删除的URL数组
    const urlsToDelete = targetBlobs.map(blob => blob.url);
    
    console.log('准备删除以下URL:', urlsToDelete);
    
    // 发送删除请求
    const deleteResponse = await fetch('/blob-api/delete', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ urls: urlsToDelete }),
    });
    
    if (!deleteResponse.ok) {
      console.error(`删除请求失败: ${deleteResponse.status} ${deleteResponse.statusText}`);
      const errorText = await deleteResponse.text();
      console.error('错误详情:', errorText);
      return false;
    }
    
    const deleteResult = await deleteResponse.json();
    console.log('删除结果:', deleteResult);
    
    // 验证删除是否成功
    const verifyResponse = await fetch(`/blob-api?prefix=banners/`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!verifyResponse.ok) {
      console.error(`验证删除失败: ${verifyResponse.status} ${verifyResponse.statusText}`);
      return true; // 假设删除成功，因为删除请求成功了
    }
    
    const verifyData = await verifyResponse.json();
    const remainingBlobs: BlobItem[] = verifyData.blobs || [];
    
    // 检查是否所有目标文件都已删除
    const allDeleted = !remainingBlobs.some(blob => 
      targetBlobs.some((target: BlobItem) => target.pathname === blob.pathname)
    );
    
    if (allDeleted) {
      console.log('验证成功：所有目标文件已删除');
      return true;
    } else {
      console.warn('验证失败：部分文件可能未删除');
      return false;
    }
    
  } catch (error) {
    console.error('删除横幅图片时出错:', error);
    return false;
  }
}; 